import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  fetchMarketLiveData,
  getMarketWebSocketUrl,
  type JsonRecord,
  type LiveMarketOutcome,
  type MarketLiveData,
  type MarketPriceHistoryPoint,
} from './api.js';

/** Market or position context selected from the dashboard. */
export type LiveModalSelection =
  | { readonly kind: 'market'; readonly marketId: string }
  | { readonly kind: 'position'; readonly marketId: string; readonly position: JsonRecord };

/** Calculated position economics at one current odds point. */
export interface PositionTakeHome {
  readonly grossTakeHomeDollars: number;
  readonly netTakeHomeDollars: number;
  readonly estimatedExitFeeDollars: number;
  readonly netPnlDollars: number;
}

/**
 * Converts an unknown value to a finite number or null.
 * @param value - Numeric-like input.
 * @returns Finite number or null.
 */
function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

/**
 * Formats a probability as venue cents.
 * @param value - Probability on a scale of one.
 * @returns Cents label.
 */
function cents(value: unknown): string {
  const numericValue = numberOrNull(value);
  return numericValue === null ? '—' : `${(numericValue * 100).toFixed(1)}¢`;
}

/**
 * Formats a dollar value for modal metrics.
 * @param value - Numeric-like dollar value.
 * @returns US dollar label.
 */
function dollars(value: unknown): string {
  const numericValue = numberOrNull(value);
  return numericValue === null ? '—' : numericValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Reads a property from an unknown JSON object.
 * @param value - Unknown JSON value.
 * @param key - Property name.
 * @returns Property value.
 */
function field(value: unknown, key: string): unknown {
  return typeof value === 'object' && value !== null ? (value as JsonRecord)[key] : undefined;
}

/**
 * Selects the odds value used for charts and position valuation.
 * @param point - Historical price point.
 * @returns Best bid, midpoint, latest trade, or null.
 */
function pointMarkPricePercent1(point: MarketPriceHistoryPoint): number | null {
  return point.bestBidPercent1 ?? point.midpointPercent1 ?? point.lastTradePricePercent1;
}

/**
 * Calculates gross and fee-adjusted take-home for a paper position.
 * @param position - Persisted position record.
 * @param currentPricePercent1 - Current liquidation odds on a scale of one.
 * @returns Position take-home economics.
 */
export function calculatePositionTakeHome(position: JsonRecord, currentPricePercent1: number): PositionTakeHome {
  const shares = numberOrNull(position.shares) ?? 0;
  const grossNotionalDollars = numberOrNull(position.gross_notional_dollars) ?? 0;
  const entryFeeDollars = numberOrNull(position.fee_dollars) ?? 0;
  const grossTakeHomeDollars = shares * currentPricePercent1;
  const takerFeeRatePercent1 = numberOrNull(position.takerFeeRatePercent1);
  const takerFeeExponent = numberOrNull(position.takerFeeExponent);
  const estimatedExitFeeDollars = takerFeeRatePercent1 === null || takerFeeExponent === null
    ? 0
    : shares * takerFeeRatePercent1 * Math.pow(currentPricePercent1 * (1 - currentPricePercent1), takerFeeExponent);
  const netTakeHomeDollars = grossTakeHomeDollars - entryFeeDollars - estimatedExitFeeDollars;
  return {
    grossTakeHomeDollars,
    netTakeHomeDollars,
    estimatedExitFeeDollars,
    netPnlDollars: netTakeHomeDollars - grossNotionalDollars,
  };
}

/**
 * Converts a latest market snapshot into append-only chart points.
 * @param outcomes - Live outcome states.
 * @param observedAt - Update receipt time.
 * @returns One point per outcome with a price state.
 */
function snapshotPricePoints(
  outcomes: readonly LiveMarketOutcome[],
  observedAt: string,
): readonly MarketPriceHistoryPoint[] {
  return outcomes.flatMap((outcome) => outcome.price === null ? [] : [{
    tokenId: outcome.tokenId,
    observedAt,
    lastTradePricePercent1: outcome.price.lastTradePricePercent1,
    bestBidPercent1: outcome.price.bestBidPercent1,
    bestAskPercent1: outcome.price.bestAskPercent1,
    midpointPercent1: outcome.price.bestBidPercent1 !== null && outcome.price.bestAskPercent1 !== null
      ? (outcome.price.bestBidPercent1 + outcome.price.bestAskPercent1) / 2
      : null,
  }]);
}

/**
 * Builds a polyline coordinate string for chronological values.
 * @param values - Timestamped numeric values.
 * @param minimumTime - Chart minimum epoch milliseconds.
 * @param maximumTime - Chart maximum epoch milliseconds.
 * @param minimumValue - Chart minimum y value.
 * @param maximumValue - Chart maximum y value.
 * @returns SVG polyline points.
 */
function polylinePoints(
  values: readonly { readonly time: number; readonly value: number }[],
  minimumTime: number,
  maximumTime: number,
  minimumValue: number,
  maximumValue: number,
): string {
  const timeRange = Math.max(1, maximumTime - minimumTime);
  const valueRange = Math.max(0.000_001, maximumValue - minimumValue);
  return values.map((point) => {
    const x = 48 + ((point.time - minimumTime) / timeRange) * 730;
    const y = 226 - ((point.value - minimumValue) / valueRange) * 202;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

/**
 * Renders a live odds chart for all market outcomes with an optional position entry marker.
 * @param props - Market outcomes, history, and optional position context.
 * @returns Odds chart.
 */
function OddsChart(props: {
  readonly outcomes: readonly LiveMarketOutcome[];
  readonly history: readonly MarketPriceHistoryPoint[];
  readonly position?: JsonRecord;
}): ReactNode {
  const colors = ['#50e3a4', '#7bb4ff', '#f6bd60', '#db8cff'];
  const entryTime = props.position === undefined ? null : new Date(String(props.position.opened_at)).getTime();
  const entryPrice = props.position === undefined ? null : numberOrNull(props.position.raw_entry_price_percent1);
  const entryTokenId = props.position === undefined ? null : String(props.position.token_id);
  const series = props.outcomes.map((outcome) => ({
    outcome,
    values: props.history
      .filter((point) => point.tokenId === outcome.tokenId)
      .map((point) => ({ time: new Date(point.observedAt).getTime(), value: point.midpointPercent1 ?? point.lastTradePricePercent1 ?? point.bestBidPercent1 }))
      .filter((point): point is { time: number; value: number } => Number.isFinite(point.time) && point.value !== null),
  }));
  const allValues = series.flatMap((entry) => entry.values);
  if (entryTime !== null && Number.isFinite(entryTime) && entryPrice !== null && entryTokenId !== null) {
    const entrySeries = series.find((candidate) => candidate.outcome.tokenId === entryTokenId);
    entrySeries?.values.push({ time: entryTime, value: entryPrice });
    entrySeries?.values.sort((left, right) => left.time - right.time);
    allValues.push({ time: entryTime, value: entryPrice });
  }
  if (allValues.length === 0) return <div className="chart-empty">Waiting for the first live odds update…</div>;
  const minimumTime = Math.min(...allValues.map((point) => point.time));
  const maximumTime = Math.max(...allValues.map((point) => point.time), minimumTime + 1);
  const entryX = entryTime === null || !Number.isFinite(entryTime)
    ? null
    : 48 + ((entryTime - minimumTime) / Math.max(1, maximumTime - minimumTime)) * 730;
  return (
    <div className="live-chart">
      <div className="chart-legend">{series.map((entry, index) => (
        <span key={entry.outcome.tokenId}><i style={{ background: colors[index % colors.length] }} />{entry.outcome.label}</span>
      ))}{entryPrice !== null && <span className="entry-legend">Entry {cents(entryPrice)}</span>}</div>
      <svg viewBox="0 0 800 260" role="img" aria-label="Live market odds chart">
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
          const y = 226 - tick * 202;
          return <g key={tick}><line x1="48" x2="778" y1={y} y2={y} className="chart-grid" /><text x="8" y={y + 4}>{Math.round(tick * 100)}¢</text></g>;
        })}
        {entryX !== null && entryX >= 48 && entryX <= 778 && <line x1={entryX} x2={entryX} y1="24" y2="226" className="entry-line" />}
        {series.map((entry, index) => entry.values.length === 0 ? null : (
          <polyline
            key={entry.outcome.tokenId}
            points={polylinePoints(entry.values, minimumTime, maximumTime, 0, 1)}
            fill="none"
            stroke={colors[index % colors.length]}
            strokeWidth="3"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
}

/**
 * Renders gross and fee-adjusted take-home history for one position.
 * @param props - Position and its token's odds history.
 * @returns Take-home chart.
 */
function TakeHomeChart(props: {
  readonly position: JsonRecord;
  readonly history: readonly MarketPriceHistoryPoint[];
}): ReactNode {
  const tokenId = String(props.position.token_id);
  const openedAt = new Date(String(props.position.opened_at)).getTime();
  const entryPricePercent1 = numberOrNull(props.position.raw_entry_price_percent1);
  const values = props.history
    .filter((point) => point.tokenId === tokenId)
    .map((point) => ({ time: new Date(point.observedAt).getTime(), price: pointMarkPricePercent1(point) }))
    .filter((point): point is { time: number; price: number } => Number.isFinite(point.time) && point.price !== null && point.time >= openedAt);
  if (Number.isFinite(openedAt) && entryPricePercent1 !== null) values.unshift({ time: openedAt, price: entryPricePercent1 });
  if (values.length === 0) return <div className="chart-empty">Waiting for post-entry odds updates…</div>;
  const takeHomes = values.map((point) => ({ time: point.time, ...calculatePositionTakeHome(props.position, point.price) }));
  const minimumTime = Math.min(...takeHomes.map((point) => point.time));
  const maximumTime = Math.max(...takeHomes.map((point) => point.time), minimumTime + 1);
  const allDollars = takeHomes.flatMap((point) => [point.grossTakeHomeDollars, point.netTakeHomeDollars]);
  const minimumValue = Math.min(...allDollars);
  const maximumValue = Math.max(...allDollars, minimumValue + 0.01);
  return (
    <div className="live-chart take-home-chart">
      <div className="chart-legend"><span><i className="gross-line" />Gross take-home</span><span><i className="net-line" />Net of entry + estimated exit fees</span></div>
      <svg viewBox="0 0 800 260" role="img" aria-label="Position take-home history chart">
        {[0, 0.5, 1].map((tick) => {
          const value = minimumValue + (maximumValue - minimumValue) * tick;
          const y = 226 - tick * 202;
          return <g key={tick}><line x1="62" x2="778" y1={y} y2={y} className="chart-grid" /><text x="4" y={y + 4}>{dollars(value)}</text></g>;
        })}
        <line x1="62" x2="62" y1="24" y2="226" className="entry-line" />
        <polyline points={polylinePoints(takeHomes.map((point) => ({ time: point.time, value: point.grossTakeHomeDollars })), minimumTime, maximumTime, minimumValue, maximumValue)} fill="none" stroke="#7bb4ff" strokeWidth="3" vectorEffect="non-scaling-stroke" />
        <polyline points={polylinePoints(takeHomes.map((point) => ({ time: point.time, value: point.netTakeHomeDollars })), minimumTime, maximumTime, minimumValue, maximumValue)} fill="none" stroke="#50e3a4" strokeWidth="3" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

/**
 * Renders current level-two depth for a selected outcome.
 * @param props - Selected live outcome.
 * @returns Depth ladder.
 */
function DepthLadder(props: { readonly outcome: LiveMarketOutcome | undefined }): ReactNode {
  const book = props.outcome?.orderBook;
  if (book === null || book === undefined) return <div className="chart-empty">Waiting for a live order-book snapshot…</div>;
  const levels = [...book.bids.slice(0, 8).map((level) => ({ ...level, side: 'bid' as const })), ...book.asks.slice(0, 8).map((level) => ({ ...level, side: 'ask' as const }))];
  const maximumNotional = Math.max(0.01, ...levels.map((level) => level.pricePercent1 * level.sizeShares));
  return (
    <div className="depth-grid">
      <div className="depth-side"><h4>Bids</h4>{book.bids.slice(0, 8).map((level) => {
        const notional = level.pricePercent1 * level.sizeShares;
        return <div className="depth-row depth-row--bid" key={level.pricePercent1}><i style={{ width: `${(notional / maximumNotional) * 100}%` }} /><span>{cents(level.pricePercent1)}</span><b>{level.sizeShares.toFixed(1)}</b><small>{dollars(notional)}</small></div>;
      })}</div>
      <div className="depth-side"><h4>Asks</h4>{book.asks.slice(0, 8).map((level) => {
        const notional = level.pricePercent1 * level.sizeShares;
        return <div className="depth-row depth-row--ask" key={level.pricePercent1}><i style={{ width: `${(notional / maximumNotional) * 100}%` }} /><span>{cents(level.pricePercent1)}</span><b>{level.sizeShares.toFixed(1)}</b><small>{dollars(notional)}</small></div>;
      })}</div>
    </div>
  );
}

/**
 * Renders a lifecycle-scoped live market or position dialog.
 * @param props - Selection and close callback.
 * @returns Modal dialog.
 */
export function LiveMarketModal(props: {
  readonly selection: LiveModalSelection;
  readonly onClose: () => void;
}): ReactNode {
  const [live, setLive] = useState<MarketLiveData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<'CONNECTING' | 'LIVE' | 'RECONNECTING'>('CONNECTING');
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(props.selection.kind === 'position' ? String(props.selection.position.token_id) : null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    void fetchMarketLiveData(props.selection.marketId).then((payload) => {
      setLive((current) => current === null ? payload : {
        ...payload,
        outcomes: current.outcomes,
        priceHistory: [...payload.priceHistory, ...current.priceHistory].slice(-2_000),
      });
      setSelectedTokenId((current) => current ?? payload.outcomes[0]?.tokenId ?? null);
      setError(null);
    }).catch((caught) => setError(caught instanceof Error ? caught.message : String(caught)));
  }, [props.selection]);

  useEffect(() => {
    let stopped = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    const connect = (): void => {
      if (stopped) return;
      setConnection((current) => current === 'CONNECTING' ? 'CONNECTING' : 'RECONNECTING');
      socket = new WebSocket(getMarketWebSocketUrl(props.selection.marketId));
      socket.addEventListener('open', () => setConnection('LIVE'));
      socket.addEventListener('message', (event) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (typeof parsed !== 'object' || parsed === null) return;
        const record = parsed as JsonRecord;
        if (record.type !== 'market-update' && record.type !== 'market-snapshot') return;
        const snapshot = record.data as Omit<MarketLiveData, 'priceHistory'>;
        const observedAt = typeof record.sentAt === 'string' ? record.sentAt : new Date().toISOString();
        setLive((current) => ({
          ...snapshot,
          priceHistory: [...(current?.priceHistory ?? []), ...snapshotPricePoints(snapshot.outcomes, observedAt)].slice(-2_000),
        }));
        setSelectedTokenId((current) => current ?? snapshot.outcomes[0]?.tokenId ?? null);
      });
      socket.addEventListener('close', () => {
        if (stopped) return;
        setConnection('RECONNECTING');
        reconnectTimer = window.setTimeout(connect, 2_000);
      });
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      socket?.close(1000, 'Live modal closed');
    };
  }, [props.selection.marketId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [props.onClose]);

  const selectedOutcome = live?.outcomes.find((outcome) => outcome.tokenId === selectedTokenId);
  const positionTakeHome = useMemo(() => {
    if (props.selection.kind !== 'position' || selectedOutcome?.price === null || selectedOutcome?.price === undefined) return null;
    const currentPricePercent1 = selectedOutcome.price.bestBidPercent1 ?? selectedOutcome.price.lastTradePricePercent1;
    return currentPricePercent1 === null ? null : calculatePositionTakeHome(props.selection.position, currentPricePercent1);
  }, [props.selection, selectedOutcome]);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <section className="live-modal" role="dialog" aria-modal="true" aria-labelledby="live-modal-title">
        <header className="modal-header">
          <div><p>{props.selection.kind === 'position' ? 'Live paper position' : 'Live tracked market'}</p><h2 id="live-modal-title">{String(field(live?.market, 'title') ?? 'Loading market…')}</h2></div>
          <div className="modal-actions"><span className={`stream-status stream-status--${connection.toLowerCase()}`}>{connection === 'LIVE' ? 'Streaming' : connection === 'CONNECTING' ? 'Connecting' : 'Reconnecting'}</span>{live !== null && <a href={live.marketUrl} target="_blank" rel="noreferrer">Open on Polymarket ↗</a>}<button ref={closeButtonRef} type="button" onClick={props.onClose} aria-label="Close live market">×</button></div>
        </header>
        {error !== null && <div className="modal-error">{error}</div>}
        {live === null ? <div className="modal-loading">Loading live odds and depth…</div> : <div className="modal-body">
          <div className="outcome-tabs" role="tablist" aria-label="Market outcomes">{live.outcomes.map((outcome) => <button type="button" role="tab" aria-selected={outcome.tokenId === selectedTokenId} className={outcome.tokenId === selectedTokenId ? 'active' : ''} key={outcome.tokenId} onClick={() => setSelectedTokenId(outcome.tokenId)}><b>{outcome.label}</b><span>{cents(outcome.price?.bestBidPercent1)} bid · {cents(outcome.price?.bestAskPercent1)} ask</span></button>)}</div>
          {props.selection.kind === 'position' && <div className="position-summary">
            <div><span>Entered</span><b>{cents(props.selection.position.raw_entry_price_percent1)}</b><small>{new Date(String(props.selection.position.opened_at)).toLocaleString()}</small></div>
            <div><span>Position size</span><b>{dollars(props.selection.position.gross_notional_dollars)}</b><small>{Number(props.selection.position.shares).toFixed(2)} shares</small></div>
            <div><span>Gross take-home now</span><b>{dollars(positionTakeHome?.grossTakeHomeDollars)}</b><small>At current best bid</small></div>
            <div><span>Net take-home now</span><b>{dollars(positionTakeHome?.netTakeHomeDollars)}</b><small>{dollars(positionTakeHome?.netPnlDollars)} net P&amp;L</small></div>
          </div>}
          <div className="modal-section"><div className="section-heading"><h3>Tracked odds</h3><span>bid/midpoint with public trades as fallback</span></div>{props.selection.kind === 'position' ? <OddsChart outcomes={live.outcomes} history={live.priceHistory} position={props.selection.position} /> : <OddsChart outcomes={live.outcomes} history={live.priceHistory} />}</div>
          {props.selection.kind === 'position' && <div className="modal-section"><div className="section-heading"><h3>Take-home history</h3><span>based on {Number(props.selection.position.shares).toFixed(2)} shares</span></div><TakeHomeChart position={props.selection.position} history={live.priceHistory} /></div>}
          <div className="modal-section"><div className="section-heading"><h3>Depth of market</h3><span>{selectedOutcome?.label ?? 'Select an outcome'} · live level 2</span></div><DepthLadder outcome={selectedOutcome} /></div>
        </div>}
      </section>
    </div>
  );
}
